import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export type WorkoutBrief = {
  id: number;
  scheduled_date: string | null;
  discipline: string | null;
  title: string | null;
  description: string | null;
  planned_duration_minutes: number | null;
  planned_distance_km: number | null;
  planned_load?: number | null;
  intensity_factor?: number | null;
  actual_distance_km: number | null;
  actual_duration_minutes: number | null;
  status: string;
  rpe: number | null;
  /** ТД-125b — целевые ориентиры (только today) */
  target_zone?: string | null;
  target_hr_max?: number | null;
  feel: string | null;
  athlete_notes: string | null;
};

export type RaceReference = {
  distance_km: number;
  time_seconds: number;
  source: string;
};

export type GoalBrief = {
  goal_type: "race" | "weight" | "strength" | "fitness_general" | "hyrox";
  race_distance: string | null;
  custom_distance_km: number | null;
  race_date: string | null;
  target_time_seconds: number | null;
  notes: string | null;
  created_at: string | null;
  target_weight_kg: number | null;
  start_weight_kg: number | null;
  target_lift_kg: number | null;
  target_lift_type: "squat" | "bench" | "deadlift" | "overhead" | null;
  weekly_sessions_target: number | null;
  predicted_seconds: number | null;
  predictions: Record<string, number> | null;
  predictions_low: Record<string, number> | null;
  predictions_high: Record<string, number> | null;
  reference: RaceReference | null;
  target_pace_per_km_seconds: number | null;
  predicted_pace_per_km_seconds: number | null;
  predicted_seconds_low: number | null;
  predicted_seconds_high: number | null;
  vdot: number | null;
  references_used: number | null;
  marathon_at_risk: boolean;
  not_peak_form: boolean;
  longest_run_km_90d: number | null;
  predictions_stale: boolean;
  reference_age_days: number | null;
  vo2max_garmin: number | null;
  vo2max_consensus: number | null;
  vo2max_disagreement: boolean;
  vo2max_used: number | null;
  vo2max_source: string | null;
  vo2max_garmin_delta_30d: number | null;
  vo2max_vdot_delta_30d: number | null;
  race_catalog_registration_url: string | null;
  race_catalog_registration_status: string | null;
  race_catalog_is_lottery: boolean;
  race_catalog_registration_opens_at: string | null;
};

export type FitnessBrief = {
  current: number;
  peak: number;
  is_new_peak: boolean;
  delta_7d: number | null;
  delta_30d: number | null;
  peak_date: string | null;
  // ТД-59: "intervals" | "storica_impulse" | "manual" — для лейбла источника.
  source?: string;
};

export type PaceBrief = {
  threshold_pace_per_km_seconds: number | null;
  easy_pace_per_km_seconds: number | null;
  marathon_pace_per_km_seconds: number | null;
  half_pace_per_km_seconds: number | null;
  ten_k_pace_per_km_seconds: number | null;
  five_k_pace_per_km_seconds: number | null;
};

export type Dashboard = {
  today: WorkoutBrief | null;
  yesterday: WorkoutBrief | null;
  color: string;
  color_reasons: string[];
  streak_current: number;
  streak_longest: number;
  completion_pct: number;
  volume_week_km: number;
  volume_week_delta_pct: number | null;
  vo2max: number | null;
  vo2max_trend: "up" | "down" | "flat" | null;
  hrv_trend: "up" | "down" | "flat" | null;
  rhr_trend: "up" | "down" | "flat" | null;
  sleep_score_trend: "up" | "down" | "flat" | null;
  sleep_score_delta_pct: number | null;
  goal: GoalBrief | null;
  recent: WorkoutBrief[];
  upcoming: WorkoutBrief[];
  fitness: FitnessBrief | null;
  pace: PaceBrief | null;
  ctl_current: number | null;
  readiness_score: number | null;
  readiness_source: string;
  /** Cycle-aware advisory (opt-in track_cycle) — фаза цикла как контекст,
   *  не модификатор readiness. null если не отслеживается. */
  cycle_phase: string | null;
  cycle_phase_advice: string | null;
};

function clientToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function useDashboard() {
  const today = clientToday();
  return useQuery<Dashboard>({
    queryKey: ["dashboard", today],
    queryFn: async () => (await api.get<Dashboard>("/athlete/dashboard", { params: { today } })).data,
    staleTime: 60_000,
  });
}

// ============================================================
// Burnout risk (Cycle 14 / G.1)
// ============================================================

export type BurnoutBand = "green" | "yellow" | "amber" | "red";

/** Individual signal contribution. value < 0 means burnout-leaning. */
export interface BurnoutRiskComponent {
  /** Component identifier — see shared/services/burnout_risk.py for the
   *  full enum. Common values: readiness_delta, tsb_current,
   *  tsb_overload_days, hrv_ln, rhr, sleep_score, stress,
   *  sleep_regularity. */
  label: string;
  value: number | null;
  weight: number;
  recent_avg: number | null;
  baseline_avg: number | null;
}

/** One layer of the multi-level composite. */
export interface BurnoutRiskLayer {
  label: "readiness_trend" | "tsb_severity" | "signal_trend";
  score: number | null;
  weight: number;
  components: BurnoutRiskComponent[];
}

export interface BurnoutRisk {
  score: number;
  band: BurnoutBand;
  components: BurnoutRiskComponent[];
  layers: BurnoutRiskLayer[];
  days_used: number;
  has_enough_data: boolean;
}

export function useBurnoutRisk() {
  return useQuery<BurnoutRisk>({
    queryKey: ["burnout-risk"],
    queryFn: async () => (await api.get<BurnoutRisk>("/athlete/burnout-risk")).data,
    staleTime: 5 * 60_000, // 5 min — wellness syncs aren't more frequent.
  });
}
